import React from 'react';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { GroupBase, OptionsOrGroups } from 'react-select';
import api from '../../../../../components/api';

function useAisleDetails() {
  // Get URL Params
  const {
    warehouseId, aisleName, level, aisleId, search,
  } = useParams();
  const [searchValue, setSearchValue] = React.useState<string>(search ?? '');

  // Get Initial Data about Aisle
  const {
    data: aisleRes, isLoading: aisleLoad, isFetching: aisleFetch, refetch: refetchAisle,
  } = useQuery(
    ['getAislesByWarehouseId', 'warehouse', warehouseId],
    () => api.warehouse.getAislesByNameAndLevel(Number(warehouseId!), aisleName!, Number(level!)),
    {
      enabled: !!warehouseId && !!aisleName && !!level,
      refetchOnWindowFocus: true,
    },
  );
  // Initial Data Handler
  const aisleData = React.useMemo(() => aisleRes?.data?.data?.items?.sort((a, b) => a.sortOrder - b.sortOrder)?.filter((row) => row?.product?.name?.toString()?.toLowerCase()?.indexOf(searchValue?.toLowerCase()) > -1) ?? [], [aisleRes, searchValue]);
  const levelId = React.useMemo(() => aisleRes?.data?.data?.id, [aisleRes]);
  console.log('aisleData', aisleData);
  // Delete Product on current Level
  const [result, setResult] = React.useState<any>();
  const { mutate: removeItem, isLoading: removeItemLoad } = useMutation(api.warehouse.removeItem, {
    onMutate: (variables) => console.log(variables),
    onSuccess: (data) => {
      console.log(data);
      setResult(data);
    },
    onError: (error) => {
      console.log(error);
      setResult(error);
    },
  });
  const handleRemoveItem = (id: number): void => removeItem(id);

  // Product Search Input Value
  const [productSearchInput, setProductSearchInput] = React.useState<string>('');
  // eslint-disable-next-line no-unused-vars
  const optionsFetcher = (text: string, callback: (options: OptionsOrGroups<{ value: any, label: string }, GroupBase<{ value: string, label: string }>>) => void) => {
    console.log('Text: ', text);
    // return wholesale 25 products
    api.product.findAll(text, 0, 25, true)
      .then((res) => {
        const resData = res?.data?.data;
        if (resData) {
          const options = resData.map((product) => ({
            value: product.id,
            label: product.name,
          }));
          callback(options);
        }
      });
  };

  // Add Product to current Level
  const [productId, setProductId] = React.useState<{ value: number | string, label: string } | null>(null);
  const { mutate: addProduct, isLoading: addProductLoad } = useMutation(api.warehouse.placeItem, {
    onMutate: (variables) => console.log(variables),
    onSuccess: (data) => {
      console.log(data);
      setResult(data);
    },
    onError: (error) => {
      console.log(error);
      setResult(error);
    },
  });
  const handleAddProduct = (i: number, condition: 'UP' | 'DOWN'): void => {
    if (!!aisleId && !!productId) {
      const data = {
        aisleId: Number(aisleId),
        productId: Number(productId.value),
      };
      if (aisleData.length === 0) {
        addProduct(data);
      } else if (condition === 'UP') {
        if (i === 0) {
          addProduct({
            ...data, next: { id: aisleData[i].id, sortOrder: aisleData[i].sortOrder },
          });
        } else {
          addProduct({
            ...data,
            next: { id: aisleData[i].id, sortOrder: aisleData[i].sortOrder },
            prev: { id: aisleData[i - 1].id, sortOrder: aisleData[i - 1].sortOrder },
          });
        }
      } else if (i === aisleData.length - 1) {
        addProduct({
          ...data, prev: { id: aisleData[i].id, sortOrder: aisleData[i].sortOrder },
        });
      } else {
        addProduct({
          ...data,
          prev: { id: aisleData[i].id, sortOrder: aisleData[i].sortOrder },
          next: { id: aisleData[i + 1].id, sortOrder: aisleData[i + 1].sortOrder },
        });
      }
    }
  };

  // Remove all item on current level
  const { mutate: clearItems, isLoading: clearItemsLoad } = useMutation(api.warehouse.removeAllItemFromLevel, {
    onMutate: (variables) => console.log(variables),
    onSuccess: (data) => {
      console.log(data);
      setResult(data);
    },
    onError: (error) => {
      console.log(error);
      setResult(error);
    },
  });
  const handleClearItems = (): void => {
    if (levelId) clearItems(levelId);
  };

  const { mutate: moveItem, isLoading: moveItemLoad } = useMutation(api.warehouse.moveItem, {
    onMutate: (variables) => console.log(variables),
    onSuccess: (data) => {
      console.log(data);
      refetchAisle().catch(() => {
        alert('error on sorting');
        window.location.reload();
      });
    },
    onError: (error) => {
      console.log(error);
      alert('error on sorting');
      window.location.reload();
    },
  });
  const handleMoveItem = (i: number, itemId: number): void => {
    if (aisleData.length > 1) {
      if (i === 0) {
        moveItem({
          itemId,
          next: {
            id: aisleData[i + 1].id, sortOrder: aisleData[i + 1].sortOrder,
          },
        });
      } else if (i === aisleData.length - 1) {
        moveItem({
          itemId,
          prev: {
            id: aisleData[i - 1].id, sortOrder: aisleData[i - 1].sortOrder,
          },
        });
      } else {
        moveItem({
          itemId,
          prev: {
            id: aisleData[i - 1].id, sortOrder: aisleData[i - 1].sortOrder,
          },
          next: {
            id: aisleData[i + 1].id, sortOrder: aisleData[i + 1].sortOrder,
          },
        });
      }
    }
  };

  const afterSubmit = () => {
    setResult(undefined);
    refetchAisle().catch((e) => console.log(e));
  };

  return {
    warehouseName: aisleName,
    searchData: {
      current: searchValue,
      setCurrent: setSearchValue,
    },
    aisle: {
      level,
      load: aisleLoad || aisleFetch || moveItemLoad,
      data: aisleData,
    },
    modalDelete: {
      result,
      submit: handleRemoveItem,
      load: removeItemLoad,
    },
    modalAddProduct: {
      result,
      submit: handleAddProduct,
      load: addProductLoad,
    },
    modalClearAllItems: {
      result,
      submit: handleClearItems,
      load: clearItemsLoad,
    },
    productId: {
      current: productId,
      setCurrent: setProductId,
      optionsFetcher,
      search: {
        current: productSearchInput,
        setCurrent: setProductSearchInput,
      },
    },
    afterSubmit,
    handleMoveItem,
  };
}

export default useAisleDetails;
